import { useEffect, useState } from "react";
import http from "../../../helpers/http";
import { doObjToFormData } from "../../../helpers/helpers";

/**
 * What this employer actually sponsored, from the Home Office release of
 * Certificates of Sponsorship by employer and occupation.
 *
 * This is the part of the page that is never empty. "Open Positions (0)" is
 * common and tells a member nothing; an employer in that file sponsored
 * something, and a third of them sponsored several different roles. So when
 * there are no vacancies this becomes the page's answer to "would they
 * sponsor someone like me", rather than the page having no answer at all.
 *
 * Fetched on its own rather than folded into company-details so the existing
 * endpoint is untouched: if this request fails the page renders exactly as it
 * does today, minus this block.
 */

// Red Hat Display is loaded as separate single-weight families ("Regular",
// "Medium", "SemiBold", "Bold" - App.css), so numeric font weights never reach
// the Bold file and the browser fakes one instead. Emphasis switches family,
// and sizes are rem on the site's own scale.
const BLUE = "#1355ff";
const INK = "#061237";
const MUTED = "rgba(6, 18, 55, 0.6)";
const FAINT = "rgba(6, 18, 55, 0.45)";

const SponsoredRoles = ({ companyName }) => {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let live = true;
    if (!companyName) return undefined;

    http
      .post("sponsor-occupations", doObjToFormData({ org_name: companyName }))
      .then(({ data }) => {
        if (!live) return;
        const list = Array.isArray(data?.occupations) ? data.occupations : [];
        setRows(list);
        setLoaded(true);
      })
      .catch(() => {
        // Silent: this block is additional, not load-bearing. The page keeps
        // working without it, and an error here must never surface as a
        // failed request on a page the member paid to see.
        if (live) setLoaded(true);
      });

    return () => {
      live = false;
    };
  }, [companyName]);

  if (!loaded || rows.length === 0) return null;

  const published = rows.filter((r) => r.cos_total !== null);
  const floor = published.reduce((n, r) => n + Number(r.cos_total || 0), 0);
  const isFloor = published.length < rows.length;

  return (
    <div
      className="sponsored_roles"
      style={{
        background: "#FFFFFF",
        borderRadius: "1.2rem",
        padding: "2.6rem 2.8rem",
        marginTop: "2rem",
        fontFamily: "Regular",
        boxShadow:
          "0 0.1rem 0.2rem rgba(6,18,55,0.04), 0 0.4rem 1.2rem rgba(6,18,55,0.06)",
      }}
    >
      <h2 className="h3" style={{ margin: "0 0 0.5rem", color: INK }}>
        What they sponsor
      </h2>
      <p style={{ margin: "0 0 1.6rem", fontSize: "1.5rem", color: MUTED }}>
        Roles this employer assigned Certificates of Sponsorship for in 2025
        {isFloor ? ", at least " : ", "}
        {floor > 0 ? `${floor} in total.` : "."}
      </p>

      {/* Column labels: "£50,000-£79,999" next to a bare "800" reads as a
          price and a quantity of something unstated. Naming both columns once
          is cheaper than repeating the words on every row. */}
      <div
        style={{
          display: "flex",
          gap: "1.4rem",
          alignItems: "baseline",
          justifyContent: "space-between",
          paddingBottom: "0.8rem",
          fontSize: "1.3rem",
          color: FAINT,
        }}
      >
        <span style={{ flex: "1 1 26rem" }}>Role and typical salary</span>
        <span style={{ flex: "0 0 auto" }}>People sponsored</span>
      </div>

      {rows.map((r) => (
        <div
          key={`${r.soc_code}-${r.soc_name}`}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "1.4rem",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "1.5rem 0",
            borderTop: "0.1rem solid rgba(6, 18, 55, 0.08)",
          }}
        >
          <div style={{ flex: "1 1 26rem", minWidth: 0 }}>
            <p
              style={{
                margin: "0 0 0.6rem",
                fontFamily: "SemiBold",
                fontSize: "1.6rem",
                lineHeight: 1.4,
                color: INK,
              }}
            >
              {r.soc_name}
            </p>
            {r.salary_band ? (
              <span
                style={{
                  display: "inline-block",
                  background: "rgba(6, 18, 55, 0.05)",
                  color: MUTED,
                  fontSize: "1.3rem",
                  padding: "0.4rem 1rem",
                  borderRadius: "0.6rem",
                }}
              >
                {r.salary_band}
              </span>
            ) : null}
          </div>
          <span
            style={{
              background: "rgba(19, 85, 255, 0.08)",
              color: BLUE,
              fontFamily: "Bold",
              fontSize: "1.5rem",
              padding: "0.7rem 1.3rem",
              borderRadius: "0.8rem",
              flex: "0 0 auto",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {r.cos_label}
          </span>
        </div>
      ))}

      <p
        style={{
          margin: "1.8rem 0 0",
          fontSize: "1.3rem",
          lineHeight: 1.7,
          color: FAINT,
        }}
      >
        Home Office data released under the Freedom of Information Act. Counts
        of five or fewer are published as a range.
      </p>
    </div>
  );
};

export default SponsoredRoles;
